import { useEffect, useState } from "react";
import { Twitter, Send, Globe, Copy, MessageSquare, Clock, User, ChevronDown, ChevronUp } from "lucide-react";
import { tokenMetaCache, type CachedMeta } from "@/lib/tokenMetaCache";
import { fetchNarrativeData } from "@/lib/narrativeFetcher";
import { useIsMobile } from "@/hooks/useIsMobile";

export function NarrativePanel({ mint }: { mint: string }) {
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState<CachedMeta | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // On desktop, start expanded; on mobile start collapsed
  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    // Initial load
    const cached = tokenMetaCache.get(mint);
    if (cached) {
      setMeta(cached);
    }
    
    // Fetch extended data
    let mounted = true;
    fetchNarrativeData(mint).then(() => {
      if (mounted) {
        setMeta(tokenMetaCache.get(mint) || null);
      }
    });

    return () => { mounted = false; };
  }, [mint]);

  if (!meta || (!meta.description && !meta.twitter && !meta.telegram && !meta.website)) {
    return null;
  }

  const handleCopy = () => {
    if (meta.creator) {
      navigator.clipboard.writeText(meta.creator);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getAge = (timestamp: number) => {
    const diffMs = Date.now() - timestamp;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const hasSocials = !!(meta.twitter || meta.telegram || meta.website);

  return (
    <div className="flex flex-col bg-black/40 border-b border-white/5 backdrop-blur-sm">

      {/* Always-visible header row: socials + toggle */}
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer select-none"
        onClick={() => setIsExpanded(v => !v)}
      >
        <div className="flex items-center gap-2">
          {/* Social icon pills - always visible */}
          {hasSocials && (
            <div className="flex gap-1.5">
              {meta.twitter && (
                <a href={meta.twitter} target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="p-1 bg-[#1DA1F2]/10 text-[#1DA1F2] rounded hover:bg-[#1DA1F2]/20 transition-colors">
                  <Twitter className="size-3" />
                </a>
              )}
              {meta.telegram && (
                <a href={meta.telegram} target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="p-1 bg-[#0088cc]/10 text-[#0088cc] rounded hover:bg-[#0088cc]/20 transition-colors">
                  <Send className="size-3" />
                </a>
              )}
              {meta.website && (
                <a href={meta.website} target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="p-1 bg-emerald-500/10 text-emerald-400 rounded hover:bg-emerald-500/20 transition-colors">
                  <Globe className="size-3" />
                </a>
              )}
            </div>
          )}
          {/* Mini description preview when collapsed */}
          {!isExpanded && meta.description && (
            <span className="text-[10px] text-white/40 truncate max-w-[180px] leading-tight">
              {meta.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {meta.creator && !isExpanded && (
            <span className="text-[9px] font-mono text-white/30">
              {meta.creator.slice(0, 4)}…{meta.creator.slice(-4)}
            </span>
          )}
          {isExpanded
            ? <ChevronUp className="size-3.5 text-white/30" />
            : <ChevronDown className="size-3.5 text-white/30" />
          }
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="flex flex-col gap-2 px-3 pb-3">
          {/* Stats Row */}
          {(meta.replyCount !== undefined || meta.createdTimestamp) && (
            <div className="flex gap-4">
              {meta.replyCount !== undefined && (
                <div className="flex items-center gap-1 text-[10px] text-white/50">
                  <MessageSquare className="size-3" />
                  <span className="font-bold">{meta.replyCount} replies</span>
                </div>
              )}
              {meta.createdTimestamp && (
                <div className="flex items-center gap-1 text-[10px] text-white/50">
                  <Clock className="size-3" />
                  <span>Deployed {getAge(meta.createdTimestamp)}</span>
                </div>
              )}
            </div>
          )}

          {/* Creator */}
          {meta.creator && (
            <div className="flex items-center gap-1.5 bg-white/5 px-2 py-1 rounded border border-white/5 cursor-pointer hover:bg-white/10 transition-colors w-fit" onClick={handleCopy} title="Copy Creator Address">
              <User className="size-3 text-white/40" />
              <span className="text-[10px] font-mono text-white/60">
                {meta.creator.slice(0, 6)}...{meta.creator.slice(-6)}
              </span>
              <Copy className={`size-3 ${copied ? "text-emerald-400" : "text-white/40"}`} />
            </div>
          )}

          {/* Description */}
          {meta.description && (
            <div className="text-[11px] leading-relaxed text-white/70 bg-white/5 p-2 rounded-lg">
              {meta.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
